import EmptyState from "@/components/empty-state/EmptyState.tsx";
import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import {
  ConfirmationModal,
  Page,
  useModals,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Stack,
  Table,
  TableCell,
} from "@skailer-com/uikit";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { EmploymentContractEditModal } from "@/pages/settings/employment-contracts/widgets/EmploymentСontractEditModal.tsx";
import {
  useEmploymentContracts,
  useEmploymentContractsDelete,
} from "@/queries/employment-contracts.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { ApiEmploymentContract } from "@/types/employment-contracts.types.ts";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";

export default function EmploymentContracts() {
  const { t } = useTranslation();
  const modalsService = useModals();

  useTitle(t("core:employment_contracts"), t("common:settings"));

  const { data: employmentContracts, isLoading, isFetching } = useEmploymentContracts();
  const employmentContractsDelete = useEmploymentContractsDelete();

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.employmentContracts.viewed);
  });

  const handleSave = useCallback((employmentContract: ApiEmploymentContract | undefined) => {
    modalsService.openModal(EmploymentContractEditModal, {
      employmentContract: employmentContract,
    });
  }, []);

  const handleDelete = useCallback((employmentContract: ApiEmploymentContract) => {
    modalsService.openModal(ConfirmationModal, {
      question: t("common:delete_confirm"),
      acceptTitle: t("common:delete"),
      onAccept: async function () {
        try {
          await employmentContractsDelete.mutateAsync(employmentContract.id);
          analyticsService.trackEvent(analyticEvents.employmentContracts.deleted, {
            [analyticProperties.id]: employmentContract.id,
          });
        } catch {
          return;
        }
      },
    });
  }, []);

  const columns = useMemo<ColumnDef<ApiEmploymentContract>[]>(
    () => [
      {
        header: t("common:title"),
        cell: (row) => <TableCell>{row.row.original.title}</TableCell>,
        accessorKey: "title",
        ...ColumnSizes.employee,
      },
      {
        header: t("core:employees"),
        cell: (row) => <div className={"text-left px-2"}>{row.row.original.employees_count}</div>,
        accessorKey: "employees_count",
        ...ColumnSizes.status,
      },
    ],
    [employmentContracts]
  );

  const renderRowAction = useCallback(
    (row: Row<ApiEmploymentContract>) => (
      <DropdownMenu>
        <DropdownMenuItem
          onClick={() => {
            handleSave(row.original);
          }}
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-pen"} />}
          title={t("common:edit")}
        />
        <DropdownMenuItem
          onClick={() => {
            handleDelete(row.original);
          }}
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
          variant={"menuDanger"}
          title={t("common:delete") + "..."}
        />
      </DropdownMenu>
    ),
    [handleDelete, handleSave, t]
  );

  return (
    <>
      <Page>
        <Page.Header
          showBack={true}
          title={t("core:employment_contracts")}
          navigateTo={"/settings/employment"}
        >
          <Stack>
            <Button
              onClick={() => handleSave(undefined)}
              leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
            >
              {t("common:create_new")}
            </Button>
          </Stack>
        </Page.Header>
        <Page.Content>
          <Table
            renderRowAction={renderRowAction}
            data={employmentContracts || []}
            columns={columns}
            isFetching={isFetching || employmentContractsDelete.isPending}
            showSkeleton={isLoading}
            notFoundComponent={<EmptyStateNothingFound />}
            emptyStateComponent={<EmptyState />}
          />
        </Page.Content>
      </Page>
    </>
  );
}
